import type { Player } from './PlayerContext'

/**
 * Whether a timecode can play right now, and if not, why.
 *
 * Kept apart from `PlayerTimecode` so the rule is a plain function: it is the
 * part that must never be wrong (a click on an unavailable timecode must not
 * reach `playFrom`), and a function can be tested without rendering anything.
 *
 * `reasonKey` is a translation key and reuses the sentences `MeetingPlayerBar`
 * already shows, so the control and the bar can never disagree about why
 * nothing plays. `loading` is the one state with no bar sentence, because it
 * is transient.
 */
export interface TimecodeAvailability {
  ready: boolean
  /** Still fetching the source. Resolves by itself, so it is not a dead end. */
  loading: boolean
  /** Null when `ready`. */
  reasonKey: string | null
}

/**
 * Whether activating a timecode will sign a fresh URL rather than fall back.
 *
 * `media-error` is the expired-credential state: the recording exists and
 * playing it needs one round trip, so sending the reader to the transcript
 * instead answers a question they did not ask.
 */
export function retriesOnActivate(player: Player | null): player is Player {
  return player !== null && !player.isReady && player.state === 'media-error'
}

export function timecodeAvailability(player: Player | null): TimecodeAvailability {
  // No player at all: the page decided this meeting cannot have audio
  // (`MeetingDetailPage` passes `enabled={hasRecording || segments > 0}`).
  if (player === null) return { ready: false, loading: false, reasonKey: 'player.unavailable' }
  if (player.isReady) return { ready: true, loading: false, reasonKey: null }
  switch (player.state) {
    case 'loading':
      return { ready: false, loading: true, reasonKey: 'player.timecodeLoading' }
    case 'unavailable':
      return { ready: false, loading: false, reasonKey: 'player.unavailable' }
    case 'not-configured':
      return { ready: false, loading: false, reasonKey: 'player.notConfigured' }
    case 'media-error':
      return { ready: false, loading: false, reasonKey: 'player.mediaError' }
    case 'forbidden':
      return { ready: false, loading: false, reasonKey: 'player.forbidden' }
    default:
      return { ready: false, loading: false, reasonKey: 'player.failed' }
  }
}

/**
 * What activating a timecode does.
 *
 * `playFrom` is reached ONLY through `isReady`. Anything else either hands the
 * moment to `onUnavailable` (a caller's fallback, e.g. opening the transcript
 * at that line) or does nothing. `loading` never reaches the fallback: it
 * resolves on its own, and a fallback taken during it would put a highlight on
 * the transcript that the player then replaces with its own position at 0:00.
 *
 * `media-error` retries with the requested moment and reaches the fallback only
 * if a fresh URL could not be signed.
 */
export function activateTimecode(
  player: Player | null,
  seconds: number,
  onUnavailable?: (seconds: number) => void,
): void {
  if (player?.isReady) {
    player.playFrom(seconds)
    return
  }
  if (retriesOnActivate(player)) {
    void player.retry(seconds).then((refreshed) => {
      if (!refreshed) onUnavailable?.(seconds)
    })
    return
  }
  if (!timecodeAvailability(player).loading) onUnavailable?.(seconds)
}
